import React, { useState, useEffect, useCallback } from 'react';
import { 
  BookOpen, ChevronRight, ChevronDown, CheckCircle, Clock, 
  Loader2, AlertCircle, Search, Layers, User, PieChart, CopyPlus,
  X, Save, Lock
} from 'lucide-react';
import apiClient from '@/utils/apiClient';
import { toast } from 'sonner';

/* ─── small helper ───────────────────────────────────────────── */
const categoryLabel = (cat) =>
  cat === 'half_yearly' ? 'Half-Yearly' : cat === 'annual' ? 'Annual' : cat;

/* ─── inline "add variant" form for a chapter ────────────────── */
const AddVariantForm = ({ chapter, subjectName, planId, onSaved, onCancel }) => {
  const [form, setForm] = useState({
    name: chapter.name,
    weightage: '',
    timeline: chapter.timeline || '',
    exam_category: chapter.exam_category === 'half_yearly' ? 'annual' : 'half_yearly',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.weightage || isNaN(parseFloat(form.weightage))) {
      toast.error('Please enter a valid weightage');
      return;
    }
    setSaving(true);
    try {
      await apiClient.post(`/v1/warden/academic/plans/${planId}/add-chapter`, {
        subject_name: subjectName,
        name: form.name,
        weightage: parseFloat(form.weightage),
        timeline: form.timeline,
        exam_category: form.exam_category,
      });
      toast.success('New category entry added!');
      onSaved();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to add entry';
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-xl space-y-2 animate-in slide-in-from-top duration-200"
    >
      <p className="text-[10px] font-black text-blue-500 uppercase tracking-widest">
        Add another exam category for "{form.name}"
      </p>

      <div className="grid grid-cols-3 gap-2">
        {/* Weightage */}
        <div className="flex flex-col gap-1">
          <label className="text-[9px] font-black text-gray-400 uppercase tracking-wider">
            Weightage
          </label>
          <input
            type="number"
            min="0"
            step="0.5"
            value={form.weightage}
            onChange={(e) => setForm(f => ({ ...f, weightage: e.target.value }))}
            placeholder="e.g. 15"
            className="px-2 py-1 text-xs border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
            required
          />
        </div>

        {/* Timeline */}
        <div className="flex flex-col gap-1">
          <label className="text-[9px] font-black text-gray-400 uppercase tracking-wider">
            Timeline
          </label>
          <input
            type="text"
            value={form.timeline}
            onChange={(e) => setForm(f => ({ ...f, timeline: e.target.value }))}
            placeholder="e.g. Aug W2"
            className="px-2 py-1 text-xs border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
          />
        </div>

        {/* Category */}
        <div className="flex flex-col gap-1">
          <label className="text-[9px] font-black text-gray-400 uppercase tracking-wider">
            Category
          </label>
          <select
            value={form.exam_category}
            onChange={(e) => setForm(f => ({ ...f, exam_category: e.target.value }))}
            className="px-2 py-1 text-xs border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
          >
            <option value="half_yearly">Half-Yearly</option>
            <option value="annual">Annual</option>
          </select>
        </div>
      </div>

      <div className="flex gap-2 pt-1">
        <button
          type="submit"
          disabled={saving}
          className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700 transition-all disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
          Save
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex items-center gap-1 px-3 py-1.5 bg-white text-gray-500 text-xs font-bold rounded-lg border hover:bg-gray-50 transition-all"
        >
          <X className="w-3 h-3" /> Cancel
        </button>
      </div>
    </form>
  );
};

/* ─── main component ─────────────────────────────────────────── */
const WardenAcademic = () => {
  const [plans, setPlans] = useState([]);
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedPlan, setExpandedPlan] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');

  // key: `${planId}-${chapterId}`  value: true/false (showing inline form)
  const [activeVariantForm, setActiveVariantForm] = useState(null);

  const canEdit = permissions?.can_update_academic === true;

  /* fetch plans + own permissions in parallel */
  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [plansRes, permRes] = await Promise.allSettled([
        apiClient.get('/v1/warden/academic/plans'),
        apiClient.get('/v1/warden/permissions/my-permissions'),
      ]);
      if (plansRes.status === 'fulfilled') setPlans(plansRes.value.data.plans || []);
      else toast.error('Failed to fetch academic plans');
      if (permRes.status === 'fulfilled') setPermissions(permRes.value.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleToggleChapter = async (planId, subjectName, chapterId, currentStatus) => {
    const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
    try {
      await apiClient.patch(`/v1/warden/academic/plans/${planId}/chapter-status`, {
        subject_name: subjectName,
        chapter_id: chapterId,
        status: newStatus
      });
      // optimistic local update
      setPlans(prev => prev.map(p => {
        if (p.id !== planId) return p;
        return {
          ...p,
          subjects: p.subjects.map(s => ({
            ...s,
            chapters: s.chapters.map(c =>
              c.id === chapterId ? { ...c, status: newStatus } : c
            )
          }))
        };
      }));
      toast.success(`Chapter marked as ${newStatus}`);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to update status';
      toast.error(detail);
    }
  };

  const getWeightage = (chap, cat) => {
    if (chap.category_weightages && chap.category_weightages[cat] !== undefined) {
      return chap.category_weightages[cat];
    }
    if (chap.exam_category === cat) return chap.weightage;
    if (chap.exam_category_2 === cat) return chap.weightage_2;
    return 0;
  };

  const calculateProgress = (subjects) => {
    if (!subjects || subjects.length === 0) return 0;
    let total = 0, done = 0;
    subjects.forEach(sub => {
      sub.chapters.forEach(chap => {
        if (filter === 'all') {
          total += (chap.weightage || 1);
          if (chap.status === 'completed') done += (chap.weightage || 1);
        } else {
          const weight = getWeightage(chap, filter);
          if (weight > 0) {
            total += weight;
            if (chap.status === 'completed') done += weight;
          }
        }
      });
    });
    return total === 0 ? 0 : Math.round((done / total) * 100);
  };

  const filteredPlans = plans.filter(p =>
    p.class_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  /* collect all unique categories across plans for filter dropdown */
  const allCategories = [...new Set(
    plans.flatMap(p => p.subjects?.flatMap(s => s.chapters?.flatMap(c => {
      const cats = [c.exam_category, c.exam_category_2];
      if (c.category_weightages) {
        cats.push(...Object.keys(c.category_weightages));
      }
      return cats.filter(Boolean);
    }) || []) || [])
  )].sort((a, b) => {
    if (a === 'half_yearly') return -1;
    if (b === 'half_yearly') return 1;
    if (a === 'annual') return -1;
    if (b === 'annual') return 1;
    return a.localeCompare(b);
  });

  if (loading && plans.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Loader2 className="w-10 h-10 text-blue-500 animate-spin mb-4" />
        <p className="text-gray-500 font-medium">Loading syllabus data...</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <BookOpen className="w-8 h-8 text-blue-500" />
            Academic Progress
          </h1>
          <p className="text-gray-500 mt-1">Monitor and update syllabus completion for all classes</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* permission badge */}
          {permissions && (
            <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold ${
              canEdit
                ? 'bg-green-100 text-green-700'
                : 'bg-amber-50 text-amber-600 border border-amber-200'
            }`}>
              {canEdit ? <CheckCircle className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
              {canEdit ? 'Edit enabled' : 'View only'}
            </span>
          )}

          {/* search */}
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search class..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none w-48 transition-all"
            />
          </div>

          {/* category filter */}
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-bold text-gray-700 focus:ring-2 focus:ring-blue-500 outline-none transition-all cursor-pointer"
          >
            <option value="all">Full Syllabus</option>
            {allCategories.map(cat => (
              <option key={cat} value={cat}>{categoryLabel(cat)} Only</option>
            ))}
          </select>
        </div>
      </div>

      {/* Class Progress Cards */}
      <div className="grid grid-cols-1 gap-6">
        {filteredPlans.length === 0 ? (
          <div className="bg-white p-12 rounded-3xl border border-dashed border-gray-200 text-center">
            <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-gray-600">No classes found</h3>
            <p className="text-gray-400">Try adjusting your search term.</p>
          </div>
        ) : (
          filteredPlans.map(plan => {
            const isExpanded = expandedPlan === plan.id;
            const progress = calculateProgress(plan.subjects);

            return (
              <div
                key={plan.id}
                className={`bg-white rounded-3xl border transition-all duration-300 ${
                  isExpanded ? 'shadow-xl border-blue-100 ring-4 ring-blue-50' : 'shadow-sm hover:shadow-md'
                }`}
              >
                {/* Card Header */}
                <div
                  className={`p-6 cursor-pointer flex flex-col md:flex-row md:items-center justify-between gap-6 ${isExpanded ? 'bg-blue-50/30' : ''}`}
                  onClick={() => setExpandedPlan(isExpanded ? null : plan.id)}
                >
                  <div className="flex items-center gap-5">
                    <div className="w-14 h-14 bg-white rounded-2xl shadow-sm border border-gray-100 flex items-center justify-center text-blue-600 font-black text-xl">
                      {plan.class_name.match(/\d+/)?.[0] || 'C'}
                    </div>
                    <div>
                      <h3 className="text-xl font-black text-gray-800">{plan.class_name}</h3>
                      <p className="text-sm text-gray-400 font-medium">
                        {plan.subjects.length} Subjects •{' '}
                        {plan.subjects.reduce((acc, s) => acc + s.chapters.length, 0)} Chapters
                      </p>
                    </div>
                  </div>

                  <div className="flex-1 max-w-md w-full">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-black text-gray-400 uppercase tracking-widest">
                        Completion Progress
                      </span>
                      <span className="text-lg font-black text-blue-600">{progress}%</span>
                    </div>
                    <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-1000 ${
                          progress === 100 ? 'bg-green-500' : 'bg-blue-500'
                        }`}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-end w-full md:w-auto">
                    {isExpanded
                      ? <ChevronDown className="w-6 h-6 text-blue-500" />
                      : <ChevronRight className="w-6 h-6 text-gray-300" />}
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="p-8 border-t border-blue-50 bg-white rounded-b-3xl space-y-10 animate-in slide-in-from-top duration-300">
                    {plan.subjects.map((sub, sIdx) => {
                      const filteredChapters = sub.chapters.filter(
                        c => filter === 'all' || 
                             c.exam_category === filter || 
                             c.exam_category_2 === filter ||
                             (c.category_weightages && c.category_weightages[filter] !== undefined)
                      );
                      if (filteredChapters.length === 0) return null;

                      const completed = filteredChapters.filter(c => c.status === 'completed').length;
                      const subProgress = Math.round((completed / filteredChapters.length) * 100);

                      return (
                        <div key={sIdx} className="space-y-4">
                          {/* Subject header */}
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 border-b border-gray-50 pb-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center text-gray-600">
                                <Layers className="w-4 h-4" />
                              </div>
                              <h4 className="text-lg font-bold text-gray-800">{sub.subject_name}</h4>
                              <span className="text-xs text-gray-400 bg-gray-50 px-2 py-1 rounded-md flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {sub.teacher_name}
                              </span>
                            </div>
                            <div className="flex items-center gap-4">
                              <span className="text-xs font-bold text-gray-500">
                                {completed}/{filteredChapters.length} Chapters Done
                              </span>
                              <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div className="h-full bg-green-400" style={{ width: `${subProgress}%` }} />
                              </div>
                            </div>
                          </div>

                          {/* Chapter cards */}
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {filteredChapters.map(chap => {
                              const variantKey = `${plan.id}-${chap.id}`;
                              const showForm = activeVariantForm === variantKey;

                              return (
                                <div
                                  key={chap.id}
                                  className={`p-4 rounded-2xl border transition-all group ${
                                    chap.status === 'completed'
                                      ? 'bg-green-50/30 border-green-100 shadow-sm'
                                      : 'bg-white border-gray-100 hover:border-blue-100'
                                  }`}
                                >
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1 min-w-0">
                                      <h5 className={`font-bold text-sm truncate ${
                                        chap.status === 'completed' ? 'text-green-800' : 'text-gray-700'
                                      }`}>
                                        {chap.name}
                                      </h5>
                                      <div className="flex flex-wrap items-center gap-2 mt-2">
                                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-tighter flex items-center gap-1">
                                          <Clock className="w-3 h-3" />
                                          {chap.timeline || '—'}
                                        </span>
                                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-tighter flex items-center gap-1">
                                          <PieChart className="w-3 h-3" />
                                          {filter === 'all' 
                                            ? (chap.weightage_2 ? `${chap.weightage}% / ${chap.weightage_2}%` : `${chap.weightage}%`)
                                            : (chap.exam_category === filter ? `${chap.weightage}%` : `${chap.weightage_2}%`)}
                                        </span>
                                        <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full uppercase tracking-tighter ${
                                          chap.exam_category === 'half_yearly'
                                            ? 'bg-purple-50 text-purple-500'
                                            : chap.exam_category === 'annual'
                                            ? 'bg-amber-50 text-amber-600'
                                            : 'bg-gray-100 text-gray-500'
                                        }`}>
                                          {categoryLabel(chap.exam_category)}
                                        </span>
                                        {chap.exam_category_2 && (
                                          <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full uppercase tracking-tighter ${
                                            chap.exam_category_2 === 'half_yearly'
                                              ? 'bg-purple-50 text-purple-500'
                                              : chap.exam_category_2 === 'annual'
                                              ? 'bg-amber-50 text-amber-600'
                                              : 'bg-gray-100 text-gray-500'
                                          }`}>
                                            {categoryLabel(chap.exam_category_2)}
                                          </span>
                                        )}
                                      </div>
                                    </div>

                                    <div className="flex items-center gap-1 flex-shrink-0">
                                      {/* Mark completed */}
                                      <button
                                        onClick={() => canEdit
                                          ? handleToggleChapter(plan.id, sub.subject_name, chap.id, chap.status)
                                          : toast.error("You don't have permission to update the academic plan")
                                        }
                                        className={`p-2 rounded-xl transition-all shadow-sm ${
                                          chap.status === 'completed'
                                            ? 'bg-green-500 text-white'
                                            : canEdit
                                              ? 'bg-white text-gray-300 border border-gray-100 hover:border-green-300 hover:text-green-500'
                                              : 'bg-gray-50 text-gray-200 border border-gray-100 cursor-not-allowed'
                                        }`}
                                        title={canEdit ? (chap.status === 'completed' ? 'Mark pending' : 'Mark completed') : 'No permission'}
                                      >
                                        <CheckCircle className="w-5 h-5" />
                                      </button>

                                      {/* Add variant — only shown when canEdit */}
                                      {canEdit && (
                                        <button
                                          onClick={() =>
                                            setActiveVariantForm(showForm ? null : variantKey)
                                          }
                                          className={`p-2 rounded-xl transition-all shadow-sm border ${
                                            showForm
                                              ? 'bg-blue-100 text-blue-600 border-blue-300'
                                              : 'bg-white text-gray-300 border-gray-100 hover:border-blue-300 hover:text-blue-500 hover:bg-blue-50'
                                          }`}
                                          title="Add another category entry for this chapter"
                                        >
                                          <CopyPlus className="w-5 h-5" />
                                        </button>
                                      )}
                                    </div>
                                  </div>

                                  {/* Inline add-variant form */}
                                  {showForm && (
                                    <AddVariantForm
                                      chapter={chap}
                                      subjectName={sub.subject_name}
                                      planId={plan.id}
                                      onSaved={() => {
                                        setActiveVariantForm(null);
                                        fetchAll();
                                      }}
                                      onCancel={() => setActiveVariantForm(null)}
                                    />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default WardenAcademic;
